/**
 * pngMeshScan.ts
 * =============================================================================
 * PNG overlay -> mesh grid scanner for the 2D display mode.
 *
 * WHY THIS EXISTS
 * ---------------
 * In 2D display mode the frontend is responsible for deriving the mesh grid
 * from customer-supplied PNG overlay images and POSTing the result to
 * `POST /api/v1/Mesh/CreateBatch2D`. The backend does NOT scan/inspect the
 * pixels; it only persists the grid it receives. All image decoding and pixel
 * walking happens here, in the browser, using the Canvas 2D API.
 *
 * CUSTOMER COLOR CONVENTION  (Redmine #8424, author: 秋本 / Akimoto)
 * -----------------------------------------------------------------
 * Each mesh-cell marker in the PNG is a small colored blob. The three color
 * channels encode data (NOT a visual color):
 *
 *   - R channel = MARKER flag. A pixel is considered part of a cell marker
 *                 when its Red is high (>= MARKER_R_THRESHOLD) AND both Green
 *                 and Blue are "low-ish" relative to Red (so ordinary
 *                 photographic/anti-aliased pixels are not mistaken for
 *                 markers). R itself carries no numeric payload.
 *   - G channel = pump car number (ポンプ車番号 / pumpNumber). 0 = no pump.
 *   - B channel = mesh row number (メッシュ番号 / row).
 *
 * LAYER
 * -----
 * The layer number is NOT stored in the pixels. It is encoded in the file
 * name. BE note: "2 ký tự cuối tên file = Layer" i.e. the layer is the last
 * numeric group of the basename. Example filenames:
 *
 *     something-01-01-01.png   -> layer 01
 *     plan-A-03-02-07.png      -> layer 07
 *
 * See `parseLayerFromFilename` for the exact (tunable) regex.
 *
 * BACKEND SIDE-EFFECTS
 * --------------------
 * The backend AUTO-CREATES Pump records from the `pumpNumber` values it
 * receives (any pumpNumber > 0 that has no matching Pump yet). The frontend
 * therefore does not need to pre-create pumps; it only needs to report the
 * pumpNumber read from the G channel.
 *
 * ⚠️ NEEDS-VALIDATION AGAINST A REAL CUSTOMER PNG ⚠️
 * --------------------------------------------------
 * Every tunable constant below (thresholds, merge radius, coordinate
 * tolerance) is a best-effort default. They MUST be validated/adjusted once a
 * real customer PNG sample is available, because the exact marker size, color
 * intensity and anti-aliasing behavior are unknown at authoring time. The
 * constants are grouped together and clearly labelled so they are easy to
 * tune. Do NOT ship to production without confirming them against real data.
 *
 * This module is pure browser + TypeScript. No React, no DOM framework.
 * =============================================================================
 */

/* ===========================================================================
 * TUNABLE CONSTANTS — NEEDS-VALIDATION against a real customer PNG sample.
 * ===========================================================================*/

/**
 * Minimum Red value for a pixel to be treated as a cell-marker pixel.
 * Higher = stricter (fewer false positives), lower = more permissive.
 * NEEDS-VALIDATION.
 */
export const MARKER_R_THRESHOLD = 200;

/**
 * A marker pixel must be "dominantly red": G and B must each be below
 * (R * MARKER_DOMINANCE_RATIO). This rejects near-white / near-yellow pixels
 * that also have a high Red value. Range 0..1; lower = stricter red dominance.
 * NEEDS-VALIDATION.
 */
export const MARKER_DOMINANCE_RATIO = 0.6;

/**
 * Alpha threshold. Fully/near transparent pixels are ignored regardless of
 * their RGB values (PNGs may have an alpha channel). NEEDS-VALIDATION.
 */
export const MIN_ALPHA = 128;

/**
 * Merge radius (in source pixels). Marker blobs are usually several adjacent
 * pixels wide; any marker pixels whose grid buckets are within this radius are
 * merged into a single logical cell whose position is the blob centroid.
 * Effectively also the assumed minimum spacing between two DISTINCT markers.
 * NEEDS-VALIDATION (depends on marker dot size in the real PNG).
 */
export const MERGE_RADIUS = 8;

/**
 * SKY-161: minimum share of the SMALLER of two cells that the other must cover for
 * an upper-layer cell to count as sitting on a lower-layer cell.
 * Measured on the customer's six PNG sets (末尾テスト, 異層(階段), 八角形, 長方形,
 * スカイシステム工事 and a perspective grid): every pair of cells that intersects at
 * all overlaps 100% of the smaller cell, and neighbouring cells never intersect (the
 * black grid lines separate them). 0.5 accepts a cell that covers more than half of
 * another — tolerating a misregistration of up to half a cell — while rejecting
 * slivers along a shared border.
 */
export const OVERLAP_RATIO = 0.5;

/**
 * Minimum size (in marker pixels) for a connected region to be treated as a
 * real mesh cell. Real cells are large filled areas; anything smaller is
 * anti-aliasing speckle at a border and is discarded. #8505 — NEEDS-VALIDATION.
 */
export const MIN_CELL_PIXELS = 12;

/* ===========================================================================
 * TYPES
 * ===========================================================================*/

/**
 * A single scanned mesh cell.
 * `x`/`y` are the pixel coordinates of the marker centroid in the SOURCE image;
 * they are retained because later steps need them to (a) draw overlay markers
 * and (b) compute under-relations between layers by pixel co-location.
 */
export interface ScannedCell {
  /** Layer number, parsed from the file name (see parseLayerFromFilename). */
  layer: number;
  /** Mesh row number (メッシュ番号), read from the Blue channel. */
  row: number;
  /** Pump car number (ポンプ車番号), read from the Green channel. 0 = no pump. */
  pumpNumber: number;
  /** X pixel coordinate of the marker centroid in the source image. */
  x: number;
  /** Y pixel coordinate of the marker centroid in the source image. */
  y: number;
  /** Bounding-box width of the cell region in source pixels (#8505). */
  w: number;
  /** Bounding-box height of the cell region in source pixels (#8505). */
  h: number;
}

/** Under-relation: cell (layer,row) sits directly above (underLayer,underRow). */
export interface UnderRelation {
  layer: number;
  row: number;
  underLayer: number;
  underRow: number;
}

/**
 * SKY-161: one scanned PNG layer — its cells plus, for every pixel, the row of the
 * cell covering it. The per-pixel map lets computeUnderRelations compare the real
 * cell SHAPES of two layers instead of their centroids.
 */
export interface ScannedLayer {
  layer: number;
  cells: ScannedCell[];
  width: number;
  height: number;
  /** Row of the cell covering pixel (y * width + x); -1 = no cell. */
  rowMap: Int16Array;
}

/** A single cell entry in the CreateBatch2D payload. */
export interface CreateBatch2DCell {
  layer: number;
  row: number;
  pumpNumber: number;
  /** Human/stable identifier: `${layer}-${row}`. */
  code: string;
  /**
   * Optional pump id. The frontend leaves this undefined; the backend resolves
   * / auto-creates the Pump from `pumpNumber`. Present only to match the exact
   * BE MeshCreateBatch2DRequest shape.
   */
  pumpId?: string;
}

/** Exact shape of the BE `MeshCreateBatch2DRequest`. */
export interface CreateBatch2DPayload {
  locationId: string;
  replaceExisting: boolean;
  cells: CreateBatch2DCell[];
  underRelations: UnderRelation[];
}

/* ===========================================================================
 * FILENAME -> LAYER
 * ===========================================================================*/

/**
 * Regex that captures the LAST group of digits in a basename.
 * Default interpretation of BE note "2 ký tự cuối tên file = Layer": the layer
 * is the final numeric group of the file name (e.g. "...-01-01-07.png" -> 07).
 *
 * NEEDS-VALIDATION: if the real convention is literally "last 2 characters" of
 * the stem rather than "last numeric group", narrow this to /(\d{2})$/ on the
 * stem instead.
 */
const LAYER_REGEX = /(\d+)(?!.*\d)/;

/**
 * Extract the layer number from a PNG file name.
 *
 * Accepts either a bare file name ("plan-01-01-07.png") or a full path
 * ("/media/plan-01-01-07.png"); the path and extension are stripped first.
 *
 * @param fileName file name or path.
 * @returns the parsed layer number.
 * @throws Error if no numeric group can be found.
 *
 * @example
 * parseLayerFromFilename("something-01-01-01.png") // => 1
 * parseLayerFromFilename("plan-A-03-02-07.png")    // => 7
 */
export function parseLayerFromFilename(fileName: string): number {
  // Strip any directory portion (handle both / and \ separators).
  const base = fileName.split(/[\\/]/).pop() ?? fileName;
  // Strip the extension (last dot onward), leaving the stem.
  const stem = base.replace(/\.[^.]*$/, '');

  const match = stem.match(LAYER_REGEX);
  if (!match) {
    throw new Error(
      `parseLayerFromFilename: no numeric layer group found in "${fileName}"`,
    );
  }
  const layer = Number.parseInt(match[1], 10);
  if (!Number.isFinite(layer)) {
    throw new Error(
      `parseLayerFromFilename: parsed layer is not a finite number in "${fileName}"`,
    );
  }
  return layer;
}

/* ===========================================================================
 * IMAGE LOADING + PIXEL WALKING
 * ===========================================================================*/

/**
 * Decode a PNG source into ImageData via an offscreen canvas.
 *
 * - `File`  : decoded with createImageBitmap when available, otherwise via an
 *             object URL + <img>. The object URL is always revoked.
 * - `string`: treated as a URL and loaded through an <img> with
 *             crossOrigin='anonymous' so getImageData does not taint the canvas
 *             (the server must send permissive CORS headers for remote URLs).
 */
export async function loadImageData(source: File | string): Promise<ImageData> {
  const drawable = await loadDrawable(source);
  const width = drawable.width;
  const height = drawable.height;

  if (width === 0 || height === 0) {
    // Release any bitmap resources before bailing out.
    disposeDrawable(drawable.value);
    throw new Error('pngMeshScan: source image has zero width or height');
  }

  const canvas = createCanvas(width, height);
  const ctx = get2DContext(canvas);
  if (!ctx) {
    disposeDrawable(drawable.value);
    throw new Error('pngMeshScan: unable to acquire 2D canvas context');
  }

  ctx.drawImage(drawable.value, 0, 0);
  disposeDrawable(drawable.value);

  return ctx.getImageData(0, 0, width, height);
}

/**
 * Minimal structural view of a 2D context — only the members we use. Both
 * CanvasRenderingContext2D and OffscreenCanvasRenderingContext2D satisfy it,
 * which lets us treat the two canvas kinds uniformly without tripping TS's
 * "union of overloads is not callable" limitation.
 */
interface MinimalContext2D {
  drawImage(image: CanvasImageSource, dx: number, dy: number): void;
  getImageData(sx: number, sy: number, sw: number, sh: number): ImageData;
}

/**
 * Acquire a 2D context from a detached canvas.
 */
function get2DContext(canvas: HTMLCanvasElement): MinimalContext2D | null {
  return canvas.getContext('2d');
}

/** Anything drawImage accepts that we produce here. */
type Drawable = { value: CanvasImageSource; width: number; height: number };

/** Load the source into a drawable (ImageBitmap or HTMLImageElement). */
async function loadDrawable(source: File | string): Promise<Drawable> {
  if (typeof source !== 'string') {
    // File / Blob input.
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(source);
      return { value: bitmap, width: bitmap.width, height: bitmap.height };
    }
    // Fallback: object URL + <img>.
    const url = URL.createObjectURL(source);
    try {
      const img = await loadHtmlImage(url, /* crossOrigin */ false);
      return {
        value: img,
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // String URL input.
  const img = await loadHtmlImage(source, /* crossOrigin */ true);
  return { value: img, width: img.naturalWidth, height: img.naturalHeight };
}

/** Load a URL into an HTMLImageElement, resolving after it has decoded. */
function loadHtmlImage(
  url: string,
  crossOrigin: boolean,
): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error(`pngMeshScan: failed to load image "${url}"`));
    img.src = url;
  });
}

/** Create a detached <canvas> for offscreen pixel reading. */
function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/** Free ImageBitmap resources; no-op for HTMLImageElement. */
function disposeDrawable(value: CanvasImageSource): void {
  if (typeof ImageBitmap !== 'undefined' && value instanceof ImageBitmap) {
    value.close();
  }
}

/**
 * Decide whether a pixel is a cell-marker pixel per the customer convention:
 * high Red, with Green and Blue each dominated by Red, and not transparent.
 */
export function isMarkerPixel(r: number, g: number, b: number, a: number): boolean {
  if (a < MIN_ALPHA) {
    return false;
  }
  if (r < MARKER_R_THRESHOLD) {
    return false;
  }
  const dominanceCeiling = r * MARKER_DOMINANCE_RATIO;
  return g <= dominanceCeiling && b <= dominanceCeiling;
}

/**
 * SKY-193: the mesh rows (Blue values) present in a decoded PNG, in one pass over the pixels.
 * Display-time replacement for {@link scanPngLayer}: Overlay2D only needed the list of rows
 * (to snap an edge pixel's Blue to a real row), and the scan cost it a second decode of the
 * same file plus a flood fill — on a 2017 phone 0.1 s for a 640×480 PNG, 1.4 s for 4800×3600.
 * A value counts as a row when at least MIN_CELL_PIXELS marker pixels carry it, the size guard
 * the scan applies to a component. Checked against the scan on every PNG registered on the
 * test host (84 files, 2026-10-24): identical row sets. Registration (打重ね画像登録) keeps
 * using the scan, which also needs the cell positions.
 */
export function markerRowsOf(imageData: ImageData): number[] {
  const { data } = imageData;
  const hist = new Uint32Array(256);
  for (let i = 0; i < data.length; i += 4) {
    if (isMarkerPixel(data[i], data[i + 1], data[i + 2], data[i + 3])) {
      hist[data[i + 2]] += 1;
    }
  }
  const rows: number[] = [];
  for (let v = 0; v < 256; v++) {
    if (hist[v] >= MIN_CELL_PIXELS) {
      rows.push(v);
    }
  }
  return rows;
}

/* ===========================================================================
 * SCAN
 * ===========================================================================*/

/**
 * Scan a PNG source into a de-duplicated list of {@link ScannedCell}.
 *
 * Algorithm (#8505 — connected components)
 * ----------------------------------------
 * 1. Decode the PNG to ImageData on an offscreen canvas.
 * 2. Build a marker mask: every pixel that passes {@link isMarkerPixel}.
 * 3. Label 4-connected components of the mask (iterative flood fill). Each
 *    contiguous red region = ONE mesh cell, however large. The customer PNG
 *    draws cells as big filled rectangles separated by black borders, so the
 *    borders (low Red) naturally split the components. Per cell we record the
 *    centroid, the bounding box, and the MODE of the Blue (row) and Green
 *    (pump) channels so anti-aliased edge pixels cannot skew the encoded value.
 * 4. Components smaller than MIN_CELL_PIXELS are dropped as speckle.
 *
 * (Old versions clustered by an 8px radius around a running centroid, which
 * shattered each large cell into ~150 markers — see BUG-004 / #8505.)
 *
 * @param source PNG File/Blob, or a URL string.
 * @param layer  layer number for every cell (usually from parseLayerFromFilename).
 * @returns the cells (one per contiguous mesh-cell region) and the per-pixel row map.
 */
export async function scanPngLayer(
  source: File | string,
  layer: number,
): Promise<ScannedLayer> {
  const imageData = await loadImageData(source);
  const { data, width, height } = imageData;
  const total = width * height;

  // 1) Build a 1-byte-per-pixel marker mask.
  const mask = new Uint8Array(total);
  for (let p = 0; p < total; p++) {
    const i = p * 4;
    if (isMarkerPixel(data[i], data[i + 1], data[i + 2], data[i + 3])) {
      mask[p] = 1;
    }
  }

  // 2) 4-connected component labelling (iterative flood fill). Each contiguous
  //    red region is ONE mesh cell — regardless of how large it is — which is
  //    what the customer PNG uses (big filled rectangles, not dot markers).
  //    row (Blue) / pumpNumber (Green) are taken as the MODE over the region so
  //    anti-aliased border pixels cannot skew the encoded value. #8505.
  const visited = new Uint8Array(total);
  const stack = new Int32Array(total); // reused index stack, avoids recursion
  const comp = new Int32Array(total); // pixels of the component being filled
  const rowMap = new Int16Array(total).fill(-1);
  const cells: ScannedCell[] = [];

  for (let start = 0; start < total; start++) {
    if (mask[start] === 0 || visited[start] === 1) {
      continue;
    }

    let sp = 0;
    stack[sp++] = start;
    visited[start] = 1;

    let count = 0;
    let sumX = 0;
    let sumY = 0;
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    const rowHist = new Uint32Array(256); // Blue channel histogram
    const pumpHist = new Uint32Array(256); // Green channel histogram

    while (sp > 0) {
      sp -= 1;
      const p = stack[sp];
      const x = p % width;
      const y = (p - x) / width;
      const i = p * 4;

      comp[count] = p;
      count += 1;
      sumX += x;
      sumY += y;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      rowHist[data[i + 2]] += 1;
      pumpHist[data[i + 1]] += 1;

      if (x > 0 && mask[p - 1] === 1 && visited[p - 1] === 0) {
        visited[p - 1] = 1; stack[sp] = p - 1; sp += 1;
      }
      if (x < width - 1 && mask[p + 1] === 1 && visited[p + 1] === 0) {
        visited[p + 1] = 1; stack[sp] = p + 1; sp += 1;
      }
      if (y > 0 && mask[p - width] === 1 && visited[p - width] === 0) {
        visited[p - width] = 1; stack[sp] = p - width; sp += 1;
      }
      if (y < height - 1 && mask[p + width] === 1 && visited[p + width] === 0) {
        visited[p + width] = 1; stack[sp] = p + width; sp += 1;
      }
    }

    if (count < MIN_CELL_PIXELS) {
      continue; // anti-aliasing speckle, not a real cell
    }

    const row = modeOf(rowHist);
    for (let j = 0; j < count; j++) {
      rowMap[comp[j]] = row;
    }

    cells.push({
      layer,
      row,
      pumpNumber: modeOf(pumpHist),
      x: Math.round(sumX / count),
      y: Math.round(sumY / count),
      w: maxX - minX + 1,
      h: maxY - minY + 1,
    });
  }

  return { layer, cells, width, height, rowMap };
}

/** Cells of one PNG layer (see {@link scanPngLayer}). */
export async function scanPngToCells(
  source: File | string,
  layer: number,
): Promise<ScannedCell[]> {
  return (await scanPngLayer(source, layer)).cells;
}

/** Index (0..255) with the highest count in a channel histogram. */
function modeOf(hist: Uint32Array): number {
  let best = 0;
  let bestCount = -1;
  for (let v = 0; v < hist.length; v++) {
    if (hist[v] > bestCount) {
      bestCount = hist[v];
      best = v;
    }
  }
  return best;
}

/* ===========================================================================
 * UNDER-RELATIONS
 * ===========================================================================*/

/**
 * Compute under-relations between an upper layer and the layer directly below.
 *
 * SKY-161: an upper cell sits on a lower cell when their pixel regions overlap by at
 * least OVERLAP_RATIO of the smaller of the two. Every pair is evaluated, so a cell
 * can sit on several cells and several cells can sit on one — what a merged
 * (two-column) cell needs in either direction. The previous version matched
 * centroids within 6px and let each lower cell be claimed once: on the customer's
 * 末尾テスト PNGs it produced 13 of the 25 real relations per layer pair, and an upper
 * cell left without a relation could be poured before the cell beneath it
 * (MeshSiteUpdateRequest only blocks a cell that has UnderMesh rows).
 *
 * Both layers must come from PNGs of the same size: they are drawn in the same
 * frame, which is what makes "same pixel" mean "same place on site".
 *
 * @param upper the higher layer.
 * @param lower the layer directly below it.
 */
export function computeUnderRelations(
  upper: ScannedLayer,
  lower: ScannedLayer,
): UnderRelation[] {
  if (upper.width !== lower.width || upper.height !== lower.height) {
    throw new Error(
      `computeUnderRelations: layer ${upper.layer} is ${upper.width}x${upper.height} ` +
        `but layer ${lower.layer} is ${lower.width}x${lower.height}; all layers must share one size`,
    );
  }

  const areaUpper = new Uint32Array(256);
  const areaLower = new Uint32Array(256);
  const overlap = new Uint32Array(256 * 256); // [upperRow * 256 + lowerRow]
  const total = upper.width * upper.height;
  for (let p = 0; p < total; p++) {
    const u = upper.rowMap[p];
    const l = lower.rowMap[p];
    if (u >= 0) areaUpper[u] += 1;
    if (l >= 0) areaLower[l] += 1;
    if (u >= 0 && l >= 0) overlap[u * 256 + l] += 1;
  }

  const relations: UnderRelation[] = [];
  for (let u = 0; u < 256; u++) {
    if (areaUpper[u] === 0) {
      continue;
    }
    for (let l = 0; l < 256; l++) {
      const shared = overlap[u * 256 + l];
      if (shared > 0 && shared >= OVERLAP_RATIO * Math.min(areaUpper[u], areaLower[l])) {
        relations.push({ layer: upper.layer, row: u, underLayer: lower.layer, underRow: l });
      }
    }
  }

  return relations;
}

/* ===========================================================================
 * PAYLOAD BUILDER
 * ===========================================================================*/

/**
 * Build the exact `MeshCreateBatch2DRequest` payload for
 * `POST /api/v1/Mesh/CreateBatch2D`.
 *
 * - Flattens `cellsByLayer` into `cells[]`, adding `code = ${layer}-${row}`.
 * - Sorts the distinct layers ascending and, for every consecutive pair,
 *   computes under-relations from the UPPER layer (L+1) down to the lower one
 *   (L) via {@link computeUnderRelations} (matching by overlapping cell shapes).
 *
 * Note: `pumpId` is intentionally left undefined on every cell — the backend
 * auto-creates/resolves Pump records from `pumpNumber`.
 *
 * @param locationId      target location id.
 * @param scannedLayers   one scanned layer per PNG (order irrelevant), from
 *                        {@link scanPngLayer}.
 * @param replaceExisting whether the backend should replace the existing 2D
 *                        mesh for this location (default true).
 */
export function buildCreateBatch2DPayload(
  locationId: string,
  scannedLayers: ScannedLayer[],
  replaceExisting = true,
): CreateBatch2DPayload {
  // Flatten -> cells[] with stable code.
  const cells: CreateBatch2DCell[] = [];
  for (const scanned of scannedLayers) {
    for (const c of scanned.cells) {
      cells.push({
        layer: c.layer,
        row: c.row,
        pumpNumber: c.pumpNumber,
        code: `${c.layer}-${c.row}`,
      });
    }
  }

  // Index the layers by layer number so consecutive layers can be paired whatever
  // order the files were selected in.
  const byLayer = new Map<number, ScannedLayer>();
  for (const scanned of scannedLayers) {
    if (byLayer.has(scanned.layer)) {
      throw new Error(`buildCreateBatch2DPayload: more than one PNG for layer ${scanned.layer}`);
    }
    byLayer.set(scanned.layer, scanned);
  }

  const layers = Array.from(byLayer.keys()).sort((a, b) => a - b);

  const underRelations: UnderRelation[] = [];
  for (let k = 0; k < layers.length - 1; k++) {
    const lower = byLayer.get(layers[k]) as ScannedLayer;
    const upper = byLayer.get(layers[k + 1]) as ScannedLayer;
    // Relation flows from the upper layer (L+1) down to the lower layer (L).
    for (const rel of computeUnderRelations(upper, lower)) {
      underRelations.push(rel);
    }
  }

  return {
    locationId,
    replaceExisting,
    cells,
    underRelations,
  };
}
